// src/composables/useExpenseList.js

import { ref, computed } from '@vue/composition-api'
import axios from '@axios'
import moment from 'moment'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { useListFilters } from '@/composables/useListFilters'

const { i18n } = useI18nUtils()
const i18nT = str => i18n(str)

export default function useExpenseList(isTemplate) {
  // 1) Table refs & data
  const refDocumentListTable = ref(null)
  const documents            = ref([])
  const totalDocuments       = ref(0)
  const perPageOptions = [25, 50, 100, 200]
  const sortBy = ref('date')
  const isSortDirDesc = ref(true)

  // 2) Filter definitions
  const defs = [
    { param: 'page',               default: 1,                                parse: raw => parseInt(raw, 10) || 1 },
    { param: 'perPage',            default: 50,                               parse: raw => parseInt(raw, 10) || 25 },
    { param: 'search',             default: '',                               parse: raw => raw },
    { param: 'invoiceType',        default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'recordType',         default: -1,								  parse: raw => parseInt(raw, 10) || 1 },
    { param: 'invoiceStatus',      default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'invoiceSeries',      default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'publicationStatus',  default: 0,                                parse: raw => parseInt(raw, 10) || 0 },
	{ param: 'attachment',  	   default: 0,                                parse: raw => parseInt(raw, 10) || 0 },
    { param: 'vendor',             default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'paymentType',        default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'paymentCategory',    default: -1,                               parse: raw => parseInt(raw, 10) || -1 },
    { param: 'startDate',          default: moment().month(0).startOf('month').format('YYYY-MM-DD'),
      parse: raw => raw },
    { param: 'endDate',            default: '',                               parse: raw => raw }
  ]

  // 3) Wire up filters (URL-sync + table reload)
  const { filterRefs, filterParams } =
      useListFilters(defs, () => refetchData())

  // 4) Alias for component binding
  const {
    page: currentPage,
    perPage,
    search: searchQuery,
    invoiceType,
    recordType,
    invoiceStatus,
    invoiceSeries,
    publicationStatus,
	attachment,
    vendor,
    paymentType,
    paymentCategory,
    startDate,
    endDate
  } = filterRefs

  // 5) Table columns
  const tableColumns = [
    { key: 'checkbox',     label: '',               thClass: 'tbl-chk',       tdClass: 'tbl-chk' },
    { key: 'number',       label: i18nT('Number'),  sortable: true,    thClass: 'pl-1', tdClass: 'pl-1' },
    { key: 'date', label: i18nT('Expense date'), sortable: true, thClass: 'sk-header-content' },
    { key: 'due_date',     label: i18nT('Due date'), sortable: true },
    { key: 'total_amount', label: i18nT('Total Amount'), sortable: true, thClass: 'sk-header-content' },
    { key: 'vendor',       label: i18nT('Vendor'),      sortable: true },
    { key: 'PaidStatus',   label: i18nT('Status'),      sortable: true },
    { key: 'DueAmount',    label: i18nT('Open balance'), sortable: true, thClass: 'sk-header-content' },
    { key: 'payment_type', label: i18nT('Payment type'), sortable: true },
    { key: 'action',       label: i18nT('Actions'),     thClass: 'tbl-actions', tdClass: 'sticky-column', stickyColumn: true }
  ]

  // 6) Data meta
  const dataMeta = computed(() => {
    const localCount = refDocumentListTable.value
        ? refDocumentListTable.value.localItems.length
        : 0
    return {
      from: totalDocuments.value === 0 ? 0 : ((currentPage.value - 1) * perPage.value + 1),
      to:   (currentPage.value - 1) * perPage.value + localCount,
      of:   totalDocuments.value
    }
  })

  // 7) Fetcher
  function fetchDocuments(ctx, callback) {
    const params = {
      page:         currentPage.value,
      show_per_page: perPage.value,
      sort_by:      ctx.sortBy || sortBy.value,
      sort_order:   (ctx.sortDesc !== undefined ? ctx.sortDesc : isSortDirDesc.value) ? 'DESC' : 'ASC',
      search:       searchQuery.value,
      start_date:   startDate.value,
      end_date:     endDate.value,
    }
    if (isTemplate) params.templates = 1

    // append other filters
    Object.entries(filterParams.value).forEach(([key, val]) => {
		//debugger;
      switch (key) {
        case 'invoiceType':     if (val !== -1) params['filters[type]'] = val; break
        case 'recordType':      if (val !== -1)  params['filters[record_type]'] = val; break
        case 'invoiceStatus':   if (val !== -1) params['filters[payment_status]'] = val; break
        case 'invoiceSeries':   if (val !== -1) params['filters[invoice_series]'] = val; break
        case 'publicationStatus': if (val === 1) params['filters[draft_only]'] = val; break
		case 'attachment': 		if (val === 1) params['filters[attachment]'] = 0; break
        case 'vendor':          if (val !== -1) params['filters[vendor]'] = val; break
        case 'paymentType':     if (val !== -1) params['filters[payment_type]'] = val; break
        case 'paymentCategory': if (val !== -1) params['filters[category]'] = val; break
        case 'startDate':       if (val) params['filters[start_date]'] = val; break
        case 'endDate':         if (val) params['filters[end_date]'] = val; break
      }
    })

    // Force expense records regardless of external filter manipulation
    params['filters[type]'] = 2
	params['filters[document_required]'] = 1

    axios.get('invoices', { params })
        .then(({ data }) => {
          documents.value = data.data.invoices
          totalDocuments.value = data.data.pagination.records_count
          callback(data.data.invoices)
        })
  }

  // 8) Manual refetch for external calls
  function refetchData() {
    refDocumentListTable.value?.refresh()
  }

  return {
    // table
    refDocumentListTable,
    tableColumns,
    dataMeta,
    fetchDocuments,
    refetchData,

    // data
    documents,
    totalDocuments,
    isSortDirDesc,
    sortBy,

    // filters for v-model
    currentPage,
    perPage,
    perPageOptions,
    searchQuery,
    invoiceType,
    recordType,
    invoiceStatus,
    invoiceSeries,
    publicationStatus,
	attachment,
    vendor,
    paymentType,
    paymentCategory,
    startDate,
    endDate
  }
}
